<template>
  <div class="folder-form-container relative w-full h-full">
    <div v-if='!isLoading' >
      <button
        class="close-folder"
        @click="activeDialog(true)"
        data-vs-button="activeDialog"
      >
        <zt-icon name="times" />
      </button>
      <div class="px-32 py-16">
        <folder-name />
        <requires-signature @signature-change="update" />
        <persist-documents v-if="signatureRequired" />
        <visible-documents v-else />
        <signature-priority v-if="signatureRequired" />
      </div>
      <div class="footer flex justify-between items-center px-10 h-24">
        <div class="flex justify-start">
          <zt-tooltip
            v-if="isUpdate"
            :active="!isDeleteAvailable"
            :text="$t('message.abm.folders.tooltipFolder')"
            position="right"
            title=""
          >
            <zt-button
              text
              class="button-cancel button-delete"
              :class="{
              'text-zt-danger': isUpdate && isDeleteAvailable,
              'cursor-pointer': isUpdate && isDeleteAvailable,
              'cursor-not-allowed': !isDeleteAvailable
            }"
              @click="activeDeleteDialog(true)"
              data-vs-button="activeDeleteDialog"
            >
              <span>{{ $t('message.abm.folders.deleteFolder') }}</span>
            </zt-button>
          </zt-tooltip>
        </div>
        <div class="justify-end">
          <zt-button
            text
            class="button-cancel mr-4"
            @click="activeDialog(true)"
            data-vs-button="activeDialog"
          >
            {{ $t('message.abm.folders.cancelFolder') }}
          </zt-button>
          <zt-button
            :disabled="!saveAvailable"
            @click="saveFolder"
            data-vs-button="saveFolder"
            :loader="loadingButton"
            loaderTxt="Cargando..."
            :class="{
            'text-zt-dark-b6': !saveAvailable,
            'text-zt-dark-200': !saveAvailable
          }"
          >{{ $t('message.abm.folders.saveFolder') }}</zt-button>
        </div>
      </div>
      <cancel-folder-dialog
        ref="modal"
        :with-folder="true"
        :is-leave="isLeave"
        :active="cancel"
      />
      <error-dialog :message="messageError" :active="isActiveErrorDialog" />
      <delete-folder-dialog
        v-if="isUpdate && isDeleteAvailable"
        :folder-name="stateFolder.params.name"
        :folder-id="stateFolder.params.id"
        :active="isActiveDeleteDialog"
      />
    </div>
    <div class="w-full h-full" v-else>
      <vue-element-loading
        :active="isLoading"
        spinner="spinner"
        duration="1.5"
        color="#4f4f4f"
        size="50"
      />
    </div>
  </div>
</template>
<script>
import VueElementLoading from 'vue-element-loading'
import FolderName from '@/modules/Folders/components/settings/folder/FolderName'
import PersistDocuments from '@/modules/Folders/components/settings/folder/PersistDocuments'
import RequiresSignature from '@/modules/Folders/components/settings/folder/RequiresSignature'
import SignaturePriority from '@/modules/Folders/components/settings/folder/SignaturePriority'
import VisibleDocuments from '@/modules/Folders/components/settings/folder/VisibleDocuments'
import CancelFolderDialog from '@/modules/Folders/components/modals/CancelFolderDialog'
import ErrorDialog from '@/modules/Folders/components/modals/ErrorDialog'
import DeleteFolderDialog from '@/modules/Folders/components/modals/DeleteFolderDialog'
import useFolderState from '@/modules/Folders/hooks/useFolderState'
import Folders from '@/apiClient/Folders'
import { isEmpty, isEqual, clone } from 'lodash'
const {
  state: stateFolder,
  resetParams,
  setParams,
  setError,
  resetError
} = useFolderState()
export default {
  name: 'FoldersABM',
  components: {
    FolderName,
    PersistDocuments,
    VisibleDocuments,
    RequiresSignature,
    SignaturePriority,
    CancelFolderDialog,
    DeleteFolderDialog,
    ErrorDialog,
    VueElementLoading
  },
  provide() {
    return {
      activeDialog: this.activeDialog,
      activeErrorDialog: this.activeErrorDialog,
      activeDeleteDialog: this.activeDeleteDialog,
      stateFolder,
      setParams,
      resetParams,
      resetError
    }
  },
  data() {
    return {
      signatureRequired: false,
      saveAvailable: false,
      stateFolder: stateFolder,
      loadingButton: false,
      cancel: false,
      id: null,
      isLeave: false,
      isLoading: false,
      cache: {},
      isActiveErrorDialog: false,
      isActiveDeleteDialog: false,
      messageError: '',
      isDeleteAvailable: false
    }
  },
  computed: {
    isUpdate() {
      return this.stateFolder.params.isUpdate
    }
  },
  watch: {
    stateFolder: {
      handler() {
        const changed = !isEqual(this.cache, this.stateFolder.params)
        if (stateFolder.params.hasBlocking && stateFolder.params.blocking.length) {
          this.saveAvailable = !!stateFolder.params.name.length && changed
        } else {
          this.saveAvailable = stateFolder.params.name.length && !stateFolder.params.hasBlocking && changed
        }
      },
      deep: true
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.saveAvailable) {
      this.isLeave = true
      this.activeDialog(true)
      this.$nextTick(() => {
        this.$refs.modal.$once('confirm-leave', async bvEvt => {
          if (bvEvt) {
            await resetParams()
            next()
          }
        })
      })
      return
    }
    next()
  },
  created() {
    window.scrollTo(0, 0)
    this.folderApi = new Folders()
    const id = this.$route.params.id
    if (id && this.$route.name === 'update') {
      setParams({ isUpdate: true })
      this.id = id
      this.getConfigurationFolder()
    }
  },
  methods: {
    update(value) {
      this.signatureRequired = value
    },
    async saveFolder() {
      try {
        this.loadingButton = true
        let folder = {
          name: stateFolder.params.name,
          visible: stateFolder.params.visible ? 1 : 0,
          firmable: stateFolder.params.signature ? 1 : 0,
          efimero: stateFolder.params.ephemeral ? 1 : 0,
          bloqueante: stateFolder.params.hasBlocking
            ? stateFolder.params.signature
              ? stateFolder.params.blocking
              : ''
            : ''
        }
        if (this.isUpdate) {
          await this.folderApi.updateFolder(this.id, folder)
          this.saveAvailable = false
          await resetParams()
          await this.$router.push({ name: 'list' })
        } else {
          const { data: { data: { id } } } = await this.folderApi.createFolder(folder)
          this.saveAvailable = false
          const folderName = stateFolder.params.name
          await resetParams()
          await this.$router.push({
            name: 'subFolderList',
            params: { id: id, folderName }
          })
        }
        this.loadingButton = false
      } catch (e) {
        if (e.response && e.response.status === 400) {
          const { code } = e.response.data.codes[0]
          const message = this.$t(`message.abm.folders.errors.${code}`)
          if (code !== '10036' && code !== '10028') {
            this.messageError = message
            this.activeErrorDialog(true)
          }
          setError({ code, message })
        }
        this.loadingButton = false
      }
    },
    async activeDialog(active) {
      if (!this.saveAvailable) {
        await resetParams()
        this.$router.push('/folder')
        return
      }
      if (!active) this.isLeave = false
      this.cancel = active
    },
    activeErrorDialog(active) {
      this.isActiveErrorDialog = active
    },
    activeDeleteDialog(active) {
      if (!this.isDeleteAvailable) {
        return
      }
      this.isActiveDeleteDialog = active
    },
    async getConfigurationFolder() {
      this.isLoading = true
      try {
        const {
          data: { data }
        } = await this.folderApi.getConfigurationFolder(this.id)
        const params = {
          id: this.id,
          deleteFolder: data.deleteFolder,
          blocking: !isEmpty(data.bloqueante) ? data.bloqueante.toString() : '',
          hasBlocking: !isEmpty(data.bloqueante) && data.bloqueante.length > 0,
          ephemeral: data.efimero,
          name: data.name,
          signature: data.firmable,
          visible: data.visible
        }
        await setParams(params)
        this.signatureRequired = params.signature
        this.isDeleteAvailable = params.deleteFolder
        this.cache = clone(this.stateFolder.params)
        this.isLoading = false
      } catch (e) {
        this.isLoading = false
      }
    }
  }
}
</script>
<style scoped lang="scss">
.folder-form-container {
  overflow-y: auto;
  .close-folder {
    font-size: 24px;
    line-height: 1;
    position: absolute;
    right: 2.5rem;
    top: 4.85rem;
  }
}
.folder-form-container::-webkit-scrollbar-track {
  background-color: #c4c4c4;
}
.folder-form-container::-webkit-scrollbar {
  width: 0.625rem;
}
.folder-form-container::-webkit-scrollbar-thumb {
  border-radius: 5px;
  background-color: #888888;
}
.footer {
  background: #E5E5E5;
  border-top: 1px solid #C4C4C4;
  font-size: 1.125rem;
  .text-zt-dark-b6 {
    color: #B6B6B6!important;
  }
  ::v-deep {
    .button-cancel {
      color: #707070!important;
      background: transparent!important;
    }
    button.button-delete {
      padding: 0!important;
    }
    .button-cancel.text-zt-danger {
      color: #f16b6c!important;
      &:hover {
        span {
          border-bottom: solid 1px #f16b6c;
          line-height: 1;
        }
      }
    }
  }
}
</style>
